import { FileCode2, Globe2, ShieldCheck, Users } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";
import { WorldNetwork } from "./WorldNetwork";

const PILLARS = [
    { icon: FileCode2, title: "Smart Contract Powered", desc: "All transactions and operations are executed through secure, automated smart contracts." },
    { icon: ShieldCheck, title: "Blockchain Security", desc: "An immutable distributed ledger ensures tamper-proof and reliable data security." },
    { icon: Globe2, title: "Global Crypto Ecosystem", desc: "A global crypto economy where value flows freely, securely and without borders." },
    { icon: Users, title: "Community Driven", desc: "A transparent, verifiable system powered by a global community." },
];

const CHIPS = ["Built on Web3 Foundation", "Binance Smart Chain — BEP20", "USDT BEP20"];

export const Ecosystem = () => {
    const { t } = useLanguage();
    return (
        <section id="ecosystem" data-testid="ecosystem-section" className="relative bg-nx-base py-20 sm:py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-12">
                    <div className="lg:col-span-6">
                        <SectionHeader
                            index="01"
                            label={t("The Ecosystem")}
                            testId="ecosystem"
                            title={<>{t("Decentralized Finance,")}<br />{t("Built for Global Scale.")}</>}
                        />
                        <Reveal delay={0.15} className="mt-7 space-y-5 text-base lg:text-lg leading-relaxed text-nx-sub">
                            <p>{t("DeFi — decentralized finance — is the future of financial freedom built on blockchain technology. Web 3.0 is decentralized and open for everyone, ensuring transparency, trust and user empowerment.")}</p>
                            <p>{t("Nexora Global is a global smart-contract ecosystem built on a Web3 foundation with next-generation infrastructure — secure, transparent and global by design.")}</p>
                        </Reveal>

                        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
                            {PILLARS.map((p, i) => (
                                <Reveal key={p.title} delay={0.1 + i * 0.07}>
                                    <div
                                        className="group h-full rounded-2xl border border-white/5 bg-nx-elev p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-white/10"
                                        data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        <p.icon size={20} strokeWidth={1.5} className="text-nx-lime" aria-hidden="true" />
                                        <h3 className="mt-4 text-sm font-extrabold tracking-wide text-nx-text">{t(p.title)}</h3>
                                        <p className="mt-2 text-sm leading-relaxed text-nx-sub">{t(p.desc)}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-6">
                        <Reveal delay={0.2} className="relative h-full">
                            <div className="relative flex h-full min-h-[420px] flex-col overflow-hidden rounded-2xl border border-white/5 bg-nx-alt">
                                <div className="bg-grid-fine absolute inset-0 opacity-70" aria-hidden="true" />
                                <WorldNetwork className="relative m-auto w-full max-w-[520px] p-6" />
                                <div className="relative flex flex-wrap items-center gap-2 border-t border-white/5 p-5">
                                    {CHIPS.map((chip) => (
                                        <span
                                            key={chip}
                                            className="rounded-full border border-white/10 bg-nx-elev px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-nx-sub"
                                        >
                                            {t(chip)}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};
